// @ts-check

const { isCustomEnterprisePlan } = require("@functions/workspacePlans.js");
const checkCreditSystemUsage = require("./checkCreditSystemUsage.js");
const { isFeatureAvailable, Feature, isNASAvailable } = require("@functions/featureAvailability");
const { FolderUsageErrorType } = require("../state/types");
const { isFolderUsageEnabled } = require("./utils/ubpUtils");

module.exports = {
    hasHardLimit(usage) {
        var hardLimit = true;

        if (isCustomEnterprisePlan(usage?.plan)) {
            hardLimit = !(usage?.limit.soft ?? true);
        }

        if (usage?.billing) {
            hardLimit = false;
        }

        return hardLimit;
    },

    hasSoftLimit(usage) {
        return !this.hasHardLimit(usage);
    },

    getImageLimits(usage, imageType) {
        const quotaUsed = (usage?.images && usage?.images?.[imageType]) || 0;
        const quotaAllowed = (usage?.limit && usage?.limit?.[imageType]) || 0;
        const quotaLeft = Math.max(quotaAllowed - quotaUsed, 0);

        return { quotaUsed, quotaAllowed, quotaLeft };
    },

    canUploadImages(usage, { imageType, numImages, planCycleUsed, apiKey }) {
        if (this.hasCreditSystem(usage)) {
            if (apiKey) {
                const enabled = isFolderUsageEnabled(apiKey);
                if (!enabled) {
                    throw new Error(FolderUsageErrorType.PAUSED);
                }
            }

            const featureName = imageType == "source" ? "image-indexing" : "image-generation";

            // image-indexing is uploading source images
            const creditCost = checkCreditSystemUsage.calculateCreditCost(
                usage,
                featureName,
                numImages
            );

            return checkCreditSystemUsage.creditSpendingAbility({
                usage,
                creditsSpendAmount: creditCost,
                planCycleUsed
            });
        }

        const limits = this.getImageLimits(usage, imageType);
        const deny = this.hasHardLimit(usage) && numImages > limits.quotaLeft;
        return !deny;
    },

    isOnLegacyPlan(usage) {
        return usage?.workspace?.plan == "legacy";
    },

    hasAccessToHealthCheck(usage) {
        // TODO:  remove the legacy check because legacy workspace have proper feature flags set?
        if (this.isOnLegacyPlan(usage)) {
            return usage?.pro;
        }

        return !!usage?.features?.healthCheck;
    },

    hasAccessToAdvancedAugmentations(usage) {
        return isFeatureAvailable(Feature.AdvancedAugmentations, usage);
    },

    hasAccessToAdvancedPreprocessing(usage) {
        return isFeatureAvailable(Feature.AdvancedPreprocessing, usage);
    },

    hasCreditSystem: checkCreditSystemUsage.hasCreditSystem,

    hasAccessToPrivateProjects(usage) {
        return !!usage?.features?.allowPrivateProjects;
    },

    hasAccessToExportVersion(usage) {
        return !!usage?.features?.exportVersion;
    },

    hasAccessToCloudMLIntegration(usage) {
        return !!usage?.features?.cloudMlIntegrations;
    },

    getAugmentationVariantLimit(usage) {
        return usage?.limit?.variantsPerImage || 0;
    },

    hasAnnotationReviewEnabled(usage) {
        // This is different from checking if the user has access to annotation review. Your plan can have access, but have it disabled. For feature availability, check `isFeatureAvailable(Feature.AnnotationReview, usage)`
        return !!usage?.features?.annotationReview;
    },

    hasWorkspaceInsights(usage) {
        return !!usage?.features?.workspaceInsights;
    },

    hasDeviceManagement(usage) {
        return !!usage?.features?.deviceMonitoring;
    },

    hasAccessToNAS(usage) {
        return isNASAvailable(usage);
    },

    hasAccessToAdvancedLabelAssist(usage) {
        return !!usage?.features?.advancedLabelAssist;
    },

    hasRBAC(usage) {
        return !!usage?.features?.rbac;
    },

    hasHighComplexitySmartPoly(usage) {
        return !!usage?.features?.highComplexitySmartPoly;
    },

    hasUnlimitedTimeDedicatedDeployments(usage) {
        return !!usage?.features?.unlimitedTimeDedicatedDeployments;
    },

    billSelfHostedDeployments(usage) {
        return !!usage?.features?.billSelfHostedDeployments;
    },

    billDeviceLogs(usage) {
        return this.hasCreditSystem(usage) && usage?.features?.billDeviceLogs !== false;
    },

    hasFolderBilling(usage) {
        return !!usage?.features?.folderBilling;
    },

    allowCustomPythonDedicatedDeployments(usage) {
        if (usage?.features?.allowCustomPythonDedicatedDeployments === undefined) {
            return true;
        }
        return usage?.features?.allowCustomPythonDedicatedDeployments;
    },

    allowCustomConfigDedicatedDeployments(usage) {
        return usage?.features?.allowCustomConfigDedicatedDeployments;
    }
};
